// eslint-disable-next-line import/no-cycle
import axios from 'axios';
import { Auth } from 'aws-amplify';
// eslint-disable-next-line import/no-cycle
import store from '@/store';

/**
 * Gets a valid access token, attempting a refresh if the current session is expired.
 * Forces sign-out if the refresh token is also expired or invalid.
 */
let refreshPromise = null;

/**
 * Returns true when the error looks like a connectivity / network problem
 * rather than an actual auth rejection (expired refresh token, revoked session, etc.).
 */
function isNetworkError(err) {
  if (!navigator.onLine) return true;
  const msg = (err && (err.message || err.code || '')) || '';
  return /network|timeout|ERR_NETWORK|ECONNABORTED|ENOTFOUND|Failed to fetch/i.test(msg);
}

async function getValidAccessToken() {
  // If a refresh is already in progress, wait for it instead of starting another
  if (refreshPromise) {
    return refreshPromise;
  }

  try {
    const session = await Auth.currentSession();
    const accessToken = session.getAccessToken();
    const exp = accessToken.getExpiration();
    const now = Math.floor(Date.now() / 1000);

    // If token is still valid, return it
    if (now < exp) {
      return accessToken.getJwtToken();
    }

    // Token expired — force Amplify to refresh using the refresh token
    refreshPromise = (async () => {
      const user = await Auth.currentAuthenticatedUser();
      const refreshedSession = await new Promise((resolve, reject) => {
        user.refreshSession(session.getRefreshToken(), (err, newSession) => {
          if (err) reject(err);
          else resolve(newSession);
        });
      });
      return refreshedSession.getAccessToken().getJwtToken();
    })();

    const token = await refreshPromise;
    return token;
  } catch (err) {
    // If the failure is a network issue (offline, timeout, DNS, etc.)
    // don't sign out — just let the request fail naturally so the user
    // stays logged in and can retry once connectivity is restored.
    if (isNetworkError(err)) {
      throw err;
    }

    // Refresh token expired or invalid — sign out via Amplify directly and redirect
    await Auth.signOut();
    localStorage.clear();
    window.location.href = '/';
    throw err;
  } finally {
    refreshPromise = null;
  }
}

function createSecuredApi(baseURL) {
  const api = axios.create({ baseURL, headers: { 'Content-Type': 'application/json' } });

  api.interceptors.response.use(
    response => response.data,
    async error => {
      const originalRequest = error.config;

      // On 401, try refreshing the token once before logging out
      // eslint-disable-next-line no-underscore-dangle
      if (error.response?.status === 401 && !originalRequest._retried) {
        // eslint-disable-next-line no-underscore-dangle, no-param-reassign
        originalRequest._retried = true;
        let newToken;
        try {
          // Attempt to get a fresh token via refresh token
          newToken = await getValidAccessToken();
        } catch (refreshErr) {
          // If it's a network error, don't sign out — just propagate the error
          // so the user stays logged in and can retry when back online.
          if (isNetworkError(refreshErr)) {
            throw refreshErr;
          }
          // Refresh token is expired/invalid — now we sign out
          await Auth.signOut();
          localStorage.clear();
          window.location.href = '/';
          throw refreshErr;
        }
        // Retry the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axios(originalRequest);
      }
      if (error.response && error.response.data) {
        throw error.response.data;
      } else {
        throw new Error(error.message || 'Network error occurred');
      }
    },
  );

  api.interceptors.request.use(async options => {
    const token = await getValidAccessToken();
    // eslint-disable-next-line no-param-reassign
    options.headers.Authorization = `Bearer ${token}`;
    if (Object.keys(store.getters.userPoolId).length > 0) {
      // eslint-disable-next-line no-param-reassign
      options.headers.UserPoolId = store.getters.userPoolId;
    }
    return options;
  });

  return api;
}

function createUnsecuredApi(baseURL) {
  const api = axios.create({ baseURL });

  api.interceptors.response.use(
    response => response.data,
    error => {
      // Handle network errors and other cases where response might be undefined
      if (error.response && error.response.data) {
        throw error.response.data;
      } else {
        // Handle network errors, timeouts, etc.
        throw new Error(error.message || 'Network error occurred');
      }
    },
  );

  return api;
}

export default createSecuredApi(process.env.VUE_APP_BE_BASE_URL);
export const SPMApi = createSecuredApi(process.env.VUE_APP_BE_SPM_URL);
export const unsecuredSPMApi = createUnsecuredApi(process.env.VUE_APP_BE_SPM_URL);
export const unsecuredApi = createUnsecuredApi(process.env.VUE_APP_BE_BASE_URL);
export const krakenApi = createUnsecuredApi(process.env.VUE_APP_KRAKEN_API_BE_BASE_URL);
export const companySuggestApi = createUnsecuredApi(process.env.VUE_APP_COMPANY_SUGGEST_BASE_URL);
export const companyLogoApi = createUnsecuredApi(process.env.VUE_APP_COMPANY_LOGO_BASE_URL);

// Unit-tests purposes only
export const self = {
  createSecuredApi,
};
